import {
  Button,
  Divider,
  Heading,
  Switch,
  useFreshworksWidget,
} from "@stacklet/ui";

import { useUserContext } from "app/contexts/UserContext";

/**
 * Ticket fields the support widget opens with, so the request arrives triaged
 * and the user has nothing to type. `type` has to match an option of the
 * Freshdesk portal's ticket Type field exactly, or the widget ignores it.
 */
const SUPPORT_PREFILL = {
  subject: "Jun0 enablement inquiry",
  description:
    "I'd like to learn more about Control+ and enabling Jun0 in Stacklet.",
  type: "Standard Support Request",
};

interface Props {
  /** Whether this deployment has a Jun0 endpoint configured. */
  isEnabled: boolean;
}

/**
 * Jun0 settings — an advertisement as much as a control panel. Jun0 ships with
 * Control+, and enabling it is a deployment change rather than a console one, so
 * the switch only ever reports the current state: it is always disabled, and a
 * deployment without Jun0 gets the pitch — what Control+ costs, and a way to ask
 * about it — where a deployment that already has it gets neither.
 */
export function Jun0Settings({ isEnabled }: Props) {
  const { currentUser } = useUserContext();
  const { open: openSupportWidget } = useFreshworksWidget(
    currentUser.email ?? "",
    currentUser.displayName ?? "",
    SUPPORT_PREFILL,
  );

  return (
    <div className="flex flex-col gap-2xl">
      <Heading className="text-display-small" level={1}>
        Jun0
      </Heading>

      <div className="flex flex-col gap-md">
        <p className="text-body-medium text-text-secondary">
          Jun0 is Stacklet&apos;s AI-powered console assistant. Ask it questions
          in natural language to author and review Cloud Custodian policies, or
          query and visualize your cloud asset inventory data — no need to write
          GraphQL or SQL by hand.
        </p>

        <div className="flex items-center justify-between gap-lg pr-xxs">
          <span className="text-label-medium-strong text-text-primary">
            Enable Jun0
          </span>
          <span className="text-body-small text-text-secondary">
            {isEnabled ? "Included in your subscription" : "Requires Control+"}
          </span>
        </div>

        <Divider />

        <div className="flex items-center justify-between gap-lg">
          <Switch isSelected={isEnabled} position="trailing" isDisabled>
            Enabled
          </Switch>
          {/*
            Styled as a link but built as a button: it opens the support widget
            in place rather than navigating, so the app Link wrapper — which
            needs a `to` or `href` — is the wrong element.
          */}
          {isEnabled ? null : (
            <Button
              className="px-0 py-0 text-link-small font-normal text-interactive-link-enabled underline hover:bg-transparent hover:text-interactive-link-hover focus:bg-transparent focus:text-interactive-link-focus"
              onPress={openSupportWidget}
              variant="tertiary"
            >
              Contact Support
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
